import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { Star, Sparkle, PaperPlaneTilt, WarningCircle, GoogleLogo } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { sentimentColor, timeAgo } from "@/lib/format";

const statusStyle = {
  new: "bg-blue-50 text-blue-600 border-blue-200",
  pending: "bg-amber-50 text-amber-600 border-amber-200",
  responded: "bg-success/10 text-success border-success/20",
};

export default function Reputation() {
  const qc = useQueryClient();
  const [source, setSource] = useState("all");
  const [sentiment, setSentiment] = useState("all");
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews", source, sentiment], queryFn: async () => (await api.get("/reviews", { params: { source, sentiment } })).data });
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});

  const genDraft = async (r) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const { data } = await api.post(`/reviews/${r.id}/draft-response`);
      setDrafts((d) => ({ ...d, [r.id]: data.response_draft }));
      toast.success("AI drafted a response");
      qc.invalidateQueries({ queryKey: ["reviews"] });
    } catch { toast.error("Draft failed"); }
    finally { setBusy((b) => ({ ...b, [r.id]: false })); }
  };

  const publish = useMutation({
    mutationFn: async ({ id, text }) => (await api.post(`/reviews/${id}/respond`, { response_draft: text })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["reviews"] }); toast.success("Response published"); },
  });

  const negatives = reviews.filter((r) => r.sentiment === "negative" && r.status !== "responded");

  return (
    <Page>
      <PageHeader title="Reputation" subtitle="AI sentiment tagging & response drafting across review sites." />

      {negatives.length > 0 && (
        <div data-testid="negative-alert" className="mb-4 flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
          <WarningCircle size={20} weight="fill" className="text-destructive shrink-0" />
          <span className="text-sm"><span className="font-medium text-destructive">{negatives.length} negative reviews</span> need a response. Prioritize these to protect your rating.</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2.5 mb-4">
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[160px] h-10" data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>{["all", "Google", "TripAdvisor", "Expedia", "Yelp"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All sources" : s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sentiment} onValueChange={setSentiment}>
          <SelectTrigger className="w-[170px] h-10" data-testid="filter-review-sentiment"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "positive", "neutral", "negative"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All sentiment" : s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {reviews.map((r, i) => {
          const draft = drafts[r.id] ?? r.response_draft ?? "";
          return (
            <div key={r.id} data-testid={`review-card-${i}`} className="fade-up rounded-lg border border-border bg-card p-5" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-0.5 text-amber-500">
                      {Array.from({ length: 5 }).map((_, j) => <Star key={j} size={15} weight={j < r.rating ? "fill" : "regular"} className={j < r.rating ? "" : "text-muted-foreground/30"} />)}
                    </div>
                    <span className="font-heading font-semibold">{r.title}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded border ${sentimentColor[r.sentiment]}`}>{r.sentiment}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${statusStyle[r.status]}`}>{r.status}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.body}</p>
                  <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                    <span className="text-xs text-muted-foreground">{r.guest_name} · {r.source} · {timeAgo(r.created_at)}</span>
                    {r.topics?.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary border border-border">{t}</span>)}
                  </div>
                </div>
              </div>

              {(draft || busy[r.id]) && (
                <div className="mt-4 rounded-md border border-border bg-secondary/40 p-3">
                  <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5"><Sparkle size={13} weight="fill" className="text-primary" /> AI response draft</div>
                  {busy[r.id] ? <div className="text-sm text-muted-foreground">Drafting a warm reply…</div> : (
                    <textarea data-testid={`review-draft-${i}`} value={draft} onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))} rows={3} className="w-full text-sm bg-background rounded-md border border-border p-2.5 outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 mt-3">
                {r.status !== "responded" && (
                  <Button data-testid={`draft-btn-${i}`} variant="secondary" size="sm" className="gap-1.5" disabled={busy[r.id]} onClick={() => genDraft(r)}>
                    <Sparkle size={14} weight="fill" /> {draft ? "Regenerate" : "AI draft"}
                  </Button>
                )}
                {draft && r.status !== "responded" && (
                  <Button data-testid={`publish-btn-${i}`} size="sm" className="gap-1.5" onClick={() => publish.mutate({ id: r.id, text: draft })}>
                    <PaperPlaneTilt size={14} weight="fill" /> Approve & publish
                  </Button>
                )}
                {r.status === "responded" && <span className="text-xs text-success font-medium">✓ Responded</span>}
              </div>
            </div>
          );
        })}
      </div>
    </Page>
  );
}
